/* ============================================================
   SAM-1074 — "Activity" overview (Business › Activity, every business of the tenant): pure helpers.
   Inputs are what api/activityStats.ts returns per business (booking stats, pass stats, upcoming
   booking rows); `buildActivityData` turns them into what the page draws. Dates are local days.
   ============================================================ */
import { BOOKING_STATUS } from '../../hooks/useBookings';
import { toDayParam } from '../../api/businessAccessCombined';
import { statusKey, type StatsResponse, type UpcomingBooking } from '../../api/activityStats';

export type DayWindow = 'today' | 'tomorrow' | 'week';

export const DAY_WINDOWS: Array<{ k: DayWindow; label: string }> = [
  { k: 'today', label: 'Today' },
  { k: 'tomorrow', label: 'Tomorrow' },
  { k: 'week', label: 'Next 7 days' },
];

const startOfDay = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d: Date, n: number): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

/** [from, to] local days of a window, both inclusive. */
export function windowDays(w: DayWindow, now: Date): { from: Date; to: Date } {
  const today = startOfDay(now);
  if (w === 'tomorrow') return { from: addDays(today, 1), to: addDays(today, 1) };
  if (w === 'week') return { from: today, to: addDays(today, 6) };
  return { from: today, to: today };
}

/** YYYY-MM-DD bounds of a window. */
export const windowRange = (w: DayWindow, now: Date): { from: string; to: string } => {
  const { from, to } = windowDays(w, now);
  return { from: toDayParam(from), to: toDayParam(to) };
};

/** "Today · Oct 21, 2026" / "Next 7 days · Oct 21 – Oct 27, 2026" (month written out). */
export function windowLabel(w: DayWindow, now: Date): string {
  const { from, to } = windowDays(w, now);
  const day = (d: Date, year: boolean) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(year ? { year: 'numeric' } : {}) });
  const name = DAY_WINDOWS.find((x) => x.k === w)!.label;
  return w === 'week' ? `${name} · ${day(from, false)} – ${day(to, true)}` : `${name} · ${day(from, true)}`;
}

/** "1:00 – 2:00 PM" for the hour that starts at `now`. */
export function nextHourLabel(now: Date): string {
  const t = (d: Date) => d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const a = t(now);
  const b = t(new Date(now.getTime() + 3_600_000));
  const [aTime, aSuffix] = a.split(' ');
  const [, bSuffix] = b.split(' ');
  return aSuffix === bSuffix ? `${aTime} – ${b}` : `${a} – ${b}`;
}

/** The statuses the Status filter offers, named as booking_status names them (`status=`): no_show, the rest lower case. */
export const STATUS_FILTERS = BOOKING_STATUS.map((s) => ({ id: s.id, label: s.label, param: statusKey(s.label) }));

const BOOKING_STATUS_BY_KEY: Record<string, number> = Object.fromEntries(BOOKING_STATUS.map((s) => [statusKey(s.label), s.id]));
const CANCELLED = statusKey('Cancelled');
/** A pass that is inside: its status is named «checked in» (compared by name: several ids share it; «checked out» is another). */
const isPassIn = (key: string): boolean => /^checked_in$/.test(key);

export interface BusinessInputs {
  id: string;
  name: string;
  /** Undefined while loading or when the request failed. */
  bookings?: StatsResponse;
  /** Counts straight from the tenant-wide stats (`by_business`), when there is no per-business stats answer. */
  counts?: { window: number | null; week: number | null; pending: number | null };
  /** Pass counts from the tenant-wide pass stats (`by_business`), when there is no per-business pass answer. */
  passCounts?: { passes: number | null; checkedIn: number | null };
  passes?: StatsResponse;
  upcoming?: UpcomingBooking[];
}

export interface DayCount { key: string; label: string; bookings: number; passes: number | null }
export interface StatusCount { id: number; label: string; count: number }

export interface BusinessRow {
  id: string;
  name: string;
  bookings: number | null;
  passes: number | null;
  nextSeven: number | null;
  pending: number | null;
  checkedIn: number | null;
  /** The next visit that has not started yet, as written in the table. */
  next: string;
}

export interface ActivityData {
  bookings: number;
  businessesWithBookings: number;
  /** null while any business's pass stats are missing: the tile shows a dash rather than a partial sum. */
  passes: number | null;
  checkedIn: number | null;
  waiting: number;
  arriving: number;
  statuses: StatusCount[];
  days: DayCount[];
  rows: BusinessRow[];
}

const inRange = (date: string, r: { from: string; to: string }): boolean => date >= r.from && date <= r.to;

const sumDays = (s: StatsResponse | undefined, r: { from: string; to: string }): number | null =>
  s ? s.by_day.filter((d) => inRange(d.date, r)).reduce((n, d) => n + d.total, 0) : null;

const sumStatus = (s: StatsResponse | undefined, r: { from: string; to: string }, pick: (key: string) => boolean): number | null =>
  s ? s.by_day.filter((d) => inRange(d.date, r)).reduce((n, d) => n + Object.entries(d.by_status).filter(([k]) => pick(statusKey(k))).reduce((m, [, v]) => m + v, 0), 0) : null;

/** A cancelled pass is not a pass of the day: «cancel» / «canceled» / «cancelled» (several spellings exist). */
const isPassCancelled = (key: string): boolean => /^cancel(l?ed)?$/.test(key);

/** Passes in the window, cancelled ones left out (by_status of each day). */
const sumPasses = (s: StatsResponse | undefined, r: { from: string; to: string }): number | null =>
  sumStatus(s, r, (k) => !isPassCancelled(k));

const dayPasses = (s: StatsResponse | undefined, key: string): number =>
  Object.entries(s?.by_day.find((d) => d.date === key)?.by_status ?? {}).filter(([k]) => !isPassCancelled(statusKey(k))).reduce((n, [, v]) => n + v, 0);

const nextLabel = (s: Date, now: Date): string => {
  const time = s.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return toDayParam(s) === toDayParam(now) ? time : `${s.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · ${time}`;
};

const total = (values: Array<number | null>): number => values.reduce<number>((n, v) => n + (v ?? 0), 0);
const allKnown = (values: Array<number | null>): boolean => values.length > 0 && values.every((v) => v !== null);

/** The passes and the checked-in ones of a `by_business` row: cancelled leave out; without by_status the total stands. */
export function passCountsOf(row: { count: number; by_status?: Record<string, number> } | undefined): { passes: number | null; checkedIn: number | null } {
  if (!row) return { passes: 0, checkedIn: 0 };
  if (!row.by_status) return { passes: row.count, checkedIn: null };
  const entries = Object.entries(row.by_status).map(([k, v]) => [statusKey(k), v] as const);
  return {
    passes: entries.filter(([k]) => !isPassCancelled(k)).reduce((n, [, v]) => n + v, 0),
    checkedIn: entries.filter(([k]) => isPassIn(k)).reduce((n, [, v]) => n + v, 0),
  };
}

/** What the tenant-wide calls gave (one request each, no per-business loop). */
export interface TenantInputs {
  /** GET /bookings/stats without business_id over the next 7 days. */
  bookings?: StatsResponse;
  /** GET /business_access/stats without business_id (entity business_access) over the next 7 days. */
  passes?: StatsResponse;
}

export function buildActivityData(businesses: BusinessInputs[], win: DayWindow, now: Date, tenant: TenantInputs = {}): ActivityData {
  const range = windowRange(win, now);
  const week = windowRange('week', now);

  const rows: BusinessRow[] = businesses.map((b) => {
    const upcoming = (b.upcoming ?? [])
      .filter((u) => u.status !== CANCELLED && u.start.getTime() >= now.getTime())
      .sort((x, y) => x.start.getTime() - y.start.getTime());
    return {
      id: b.id, name: b.name,
      bookings: b.counts ? b.counts.window : sumDays(b.bookings, range),
      passes: b.passCounts ? b.passCounts.passes : sumPasses(b.passes, range),
      nextSeven: b.counts ? b.counts.week : sumDays(b.bookings, week),
      pending: b.counts ? b.counts.pending : sumStatus(b.bookings, week, (k) => k === 'pending'),
      checkedIn: b.passCounts ? b.passCounts.checkedIn : sumStatus(b.passes, range, isPassIn),
      next: upcoming[0] ? nextLabel(upcoming[0].start, now) : '—',
    };
  });

  const statusTotals: Record<number, number> = {};
  (tenant.bookings ? [tenant.bookings] : businesses.map((b) => b.bookings)).forEach((st) => st?.by_day.filter((d) => inRange(d.date, range)).forEach((d) => {
    Object.entries(d.by_status).forEach(([k, v]) => {
      const id = BOOKING_STATUS_BY_KEY[statusKey(k)];
      if (id) statusTotals[id] = (statusTotals[id] ?? 0) + v;
    });
  }));

  const days: DayCount[] = Array.from({ length: 7 }, (_, i) => {
    const date = addDays(startOfDay(now), i);
    const key = toDayParam(date);
    const bookings = tenant.bookings
      ? tenant.bookings.by_day.find((d) => d.date === key)?.total ?? 0
      : total(businesses.map((b) => b.bookings?.by_day.find((d) => d.date === key)?.total ?? 0));
    const passKnown = businesses.length > 0 && businesses.every((b) => b.passes);
    return {
      key, bookings,
      passes: tenant.passes
        ? dayPasses(tenant.passes, key)
        : passKnown ? total(businesses.map((b) => dayPasses(b.passes, key))) : null,
      label: `${date.toLocaleDateString('en-US', { weekday: 'short' })} ${date.getDate()}`,
    };
  });

  const arriving = businesses.flatMap((b) => b.upcoming ?? []).filter((u) =>
    u.start.getTime() >= now.getTime() && u.start.getTime() < now.getTime() + 3_600_000
    && (u.status === 'pending' || u.status === 'confirmed')).length;

  return {
    bookings: tenant.bookings ? (sumDays(tenant.bookings, range) ?? 0) : total(rows.map((r) => r.bookings)),
    businessesWithBookings: rows.filter((r) => (r.bookings ?? 0) > 0).length,
    passes: allKnown(rows.map((r) => r.passes)) ? total(rows.map((r) => r.passes)) : null,
    checkedIn: allKnown(rows.map((r) => r.checkedIn)) ? total(rows.map((r) => r.checkedIn)) : null,
    waiting: tenant.bookings ? (sumStatus(tenant.bookings, week, (k) => k === 'pending') ?? 0) : total(rows.map((r) => r.pending)),
    arriving,
    statuses: BOOKING_STATUS.map((s) => ({ id: s.id, label: s.label, count: statusTotals[s.id] ?? 0 })),
    days,
    rows,
  };
}

/** CSV of the by-business table (Export CSV). */
export function activityCsv(rows: BusinessRow[]): string {
  const esc = (v: string | number | null) => {
    const s = v === null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const head = ['Business', 'Bookings', 'Passes', 'Next 7 days', 'Pending', 'Checked in', 'Next'];
  return [head.join(','), ...rows.map((r) => [r.name, r.bookings, r.passes, r.nextSeven, r.pending, r.checkedIn, r.next].map(esc).join(','))].join('\n');
}
